import {
  Actor,
  Collider,
  Falling,
  Instability,
  Lifetime,
  Shockwave,
  Static,
  Transform,
  Velocity,
  VortexField,
} from '@/components'
import type { SystemContext } from '@/core/ecs/Scheduler'
import { VORTEX, WAVE } from '@/game/abilities/definitions'
import { applyKnockback, explode } from '@/game/abilities/handlers'
import { applyGrip } from '@/game/physics/grip'

/**
 * Advances every area-of-effect that persists across steps: expanding shock
 * rings and vortex anomalies.
 *
 * Rings sweep outward and hit each body exactly once, recorded in `hits` — a
 * ring that re-hit on every step would multiply its impulse by the frame rate,
 * the classic "my ability is stronger on a fast PC" bug.
 */
export function fieldSystem({ world, dt }: SystemContext): void {
  updateShockwaves(world, dt)
  updateVortexFields(world, dt)
  updateLifetimes(world, dt)
}

function updateShockwaves(world: SystemContext['world'], dt: number): void {
  const targets = world
    .query([Transform, Velocity, Collider], { none: [Static, Falling] })
    .entities()

  for (const e of world.query([Shockwave]).entities()) {
    const wave = world.read(e, Shockwave)
    const prevRadius = wave.radius
    wave.radius = Math.min(wave.maxRadius, wave.radius + wave.speed * dt)

    for (const target of targets) {
      if (target === wave.owner || wave.hits.includes(target)) continue
      const t = world.read(target, Transform)
      const col = world.read(target, Collider)
      const dx = t.x - wave.x
      const dz = t.z - wave.z
      const dist = Math.hypot(dx, dz)

      // The ring only catches what its leading edge swept past this step.
      if (dist - col.radius > wave.radius || dist + col.radius < prevRadius) continue

      wave.hits.push(target)
      const falloff = Math.max(0, 1 - dist / wave.maxRadius)
      const isActor = world.has(target, Actor)
      // Interpolate the base, then scale by this victim's own instability —
      // the ring is one event but every fighter it catches is launched
      // according to how battered they personally are.
      const base = wave.powerEdge + (wave.power - wave.powerEdge) * falloff
      const scaling =
        WAVE.launchEdge.scaling +
        (WAVE.launch.scaling - WAVE.launchEdge.scaling) * falloff
      const instability = isActor ? (world.get(target, Instability)?.value ?? 0) : 0
      const power = base + scaling * (instability / 100)
      const dirX = dist < 1e-4 ? 0 : dx / dist
      const dirZ = dist < 1e-4 ? 1 : dz / dist

      applyKnockback(
        world,
        target,
        dirX,
        dirZ,
        power,
        isActor ? wave.instability : 0,
        'wave',
        wave.owner,
        t.x,
        t.z,
      )
    }

    if (wave.radius >= wave.maxRadius) world.destroyEntity(e)
  }
}

function updateVortexFields(world: SystemContext['world'], dt: number): void {
  const targets = world
    .query([Transform, Velocity, Collider], { none: [Static, Falling] })
    .entities()

  for (const e of world.query([VortexField, Transform]).entities()) {
    const field = world.read(e, VortexField)
    const tr = world.read(e, Transform)
    field.timer -= dt

    if (field.phase === 'arming') {
      if (field.timer <= 0) {
        field.phase = 'pulling'
        field.timer = VORTEX.pullDuration
      }
      continue
    }

    if (field.phase === 'pulling') {
      for (const target of targets) {
        const t = world.read(target, Transform)
        const vel = world.read(target, Velocity)
        const dx = tr.x - t.x
        const dz = tr.z - t.z
        const dist = Math.hypot(dx, dz)
        if (dist > field.radius || dist < 1e-3) continue

        // A grip, not an acceleration. The old code added 30 m/s² toward the
        // centre against a fighter who accelerates at 62 — the victim walked
        // out twice as fast as they were dragged, so the anomaly never held
        // anyone. See `applyGrip` and docs/PHYSICS.md §6.6.
        //
        // It deliberately applies to the caster too: the ability has to carry a
        // risk of its own.
        const falloff = 0.35 + 0.65 * (1 - dist / field.radius)
        applyGrip(vel, dx, dz, VORTEX.gripSpeed, field.pull * 0.6 * falloff, dt)
      }

      if (field.timer <= 0) {
        field.phase = 'burst'
        explode(
          world,
          tr.x,
          tr.z,
          field.radius,
          field.burstPower,
          field.instability,
          'vortex',
          field.owner,
        )
        // Keep the entity one more beat so the visual can play its collapse.
        world.add(e, Lifetime, { remaining: 0.45 })
      }
    }
  }
}

function updateLifetimes(world: SystemContext['world'], dt: number): void {
  for (const e of world.query([Lifetime]).entities()) {
    const life = world.read(e, Lifetime)
    life.remaining -= dt
    if (life.remaining <= 0) world.destroyEntity(e)
  }
}
